export interface Reachability {
  id: number;
  name: string;
  destination: string;
  type: ReachabilityType;
  publiclyAvailable: boolean;
  callNotificationEmailFormat: string | null;
}

/**
 * Shown when a reachability was created but the response carried no usable id.
 * Guards the "Create & Add" flows against POSTing an empty `{ reachability: {} }`
 * (the EB-4148 failure mode). Shared so both consumers use the identical message.
 */
export const REACHABILITY_NO_ID_ERROR =
  'Reachability was created but returned no id — cannot add it.';

export type ReachabilityType =
  | 'EMAIL'
  | 'FAX'
  | 'SMS'
  | 'TELEFON'
  | 'HANDY'
  | 'BUSY'
  | 'DIREKT'
  | 'PUSH'
  | 'WEBHOOK'
  | 'WHATSAPP';

/** Types not managed by CMT — read-only display */
export const EXTERNAL_TYPES: ReachabilityType[] = ['PUSH', 'WEBHOOK', 'WHATSAPP'];

/** Busy forwarding destinations matching this pattern are system-generated (answering machine).
 *  Pattern: 99{lang}6... where {lang} is a 2-letter language code (e.g. de, en) */
export function isSystemGeneratedBusy(r: Reachability): boolean {
  return r.type === 'BUSY' && /^99[a-z]{2}6/.test(r.destination);
}

/** Answering-machine routing forwardings (DIREKT named "AB-…") are system-managed —
 *  editing/deleting them would break the AB menu, so they are locked read-only. */
export function isSystemAbForwarding(r: Reachability): boolean {
  return r.type === 'DIREKT' && /^AB-/i.test(r.name);
}

export interface ReachabilityGroup {
  key: string;
  label: string;
  icon: string;
  types: ReachabilityType[];
  readOnly: boolean;
  items: Reachability[];
}

/** Ordered group definitions for the UI */
export const REACHABILITY_GROUPS: Omit<ReachabilityGroup, 'items'>[] = [
  { key: 'email', label: 'Email Notifications', icon: 'email', types: ['EMAIL'], readOnly: false },
  { key: 'faxsms', label: 'Fax & SMS Notifications', icon: 'sms', types: ['FAX', 'SMS'], readOnly: false },
  { key: 'manual', label: 'Manual Forwardings', icon: 'phone_forwarded', types: ['TELEFON', 'HANDY'], readOnly: false },
  { key: 'direct', label: 'Direct Forwardings', icon: 'call_made', types: ['DIREKT'], readOnly: false },
  { key: 'busy', label: 'Busy Forwardings', icon: 'phone_missed', types: ['BUSY'], readOnly: false },
  { key: 'external', label: 'External (read-only)', icon: 'cloud', types: ['PUSH', 'WEBHOOK', 'WHATSAPP'], readOnly: true },
];

/** Email format options for EMAIL type reachabilities */
export const EMAIL_FORMAT_OPTIONS = [
  { value: 'TEXT', label: 'Text only' },
  { value: 'HTML', label: 'HTML + Text' },
  { value: 'XML', label: 'XML' },
  { value: 'HTML_AND_XML', label: 'HTML + XML' },
  { value: 'HTML_AND_OPENIMMO', label: 'HTML + OpenImmo' },
];
